import { InsightsConfig } from "@/components/insights-dock";
import { INSIGHTS_SUGGESTIONS } from "@/lib/insights-suggestions";
import { EnableLoggingOverlay } from "@/components/EnableLoggingOverlay";
import { LOG_DATA_RETENTION_MESSAGE } from "@/components/observe/LoggingPageHeader";
import { useObservabilityMcpConfig } from "@/hooks/useObservabilityMcpConfig";
import { useLogsEnabledErrorCheck } from "@/hooks/useLogsEnabled";
import type { ChatOverview } from "@gram/client/models/components/chatoverview.js";
import {
  AccountType,
  HasRisk,
  Pinned,
  SortBy,
  SortOrder as ApiSortOrder,
} from "@gram/client/models/operations/listchats";
import { useAssistantsGet } from "@gram/client/react-query/assistantsGet.js";
import { useChatDeleteMutation } from "@gram/client/react-query/chatDelete.js";
import { useListChatSources } from "@gram/client/react-query/listChatSources.js";
import {
  invalidateAllListChats,
  useListChats,
} from "@gram/client/react-query/listChats.js";
import { formatPlatform } from "@/lib/formatPlatform";
import { Badge } from "@/components/ui/Badge";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { hashKey, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pin } from "lucide-react";
import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router";
import { useRBAC } from "@/hooks/useRBAC";
import { useOrganization, useProject } from "@/contexts/Auth";
import { useSdkClient } from "@/contexts/Sdk";
import { useOrgRoutes } from "@/routes";
import { ChatDetailSheet } from "@/pages/chatLogs/ChatDetailPanel";
import { ChatLogsTable } from "@/pages/chatLogs/ChatLogsTable";
import type { SortDescriptor } from "@/components/ui/Table";
import {
  defineFilters,
  type FilterValue,
  type OptionsById,
} from "@/components/filters";
import { ResourceListPage } from "@/components/page-templates";
import type { Scope } from "@gram/client/models/components/rolegrant.js";
import { type DateRangePreset, getPresetRange } from "@/elements";
import { isValidPreset } from "@/components/observe/observeFilterUtils";
import {
  AGENT_SESSION_CHAT_PARAM,
  AGENT_SESSIONS_AGENT_PARAM,
} from "@/pages/chatLogs/agentSessionLink";

type SortField = "chronological" | "messageCount";
type SortOrder = "asc" | "desc";
type SessionsView = "all" | "pinned";

function toApiSortBy(field: SortField): SortBy {
  switch (field) {
    case "chronological":
      return SortBy.LastMessageTimestamp;
    case "messageCount":
      return SortBy.NumMessages;
  }
}

function toApiHasRisk(value: string): HasRisk | undefined {
  if (value === "true") return HasRisk.True;
  if (value === "false") return HasRisk.False;
  return undefined;
}

function toApiAccountType(value: string): AccountType | undefined {
  if (value === "team") return AccountType.Team;
  if (value === "personal") return AccountType.Personal;
  return undefined;
}

function toApiPinned(value: string): Pinned | undefined {
  if (value === "true") return Pinned.True;
  if (value === "false") return Pinned.False;
  return undefined;
}

// Read the min-risk-score URL param. Empty, non-integer, or < 1 is treated as
// "no threshold" — a minimum of 0 means "≥ 0", i.e. everything, so it's
// indistinguishable from no filter (and the API rejects it).
function parseMinRiskScore(value: string | null): number | undefined {
  const trimmed = (value ?? "").trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 1) return undefined;
  return n;
}

function toApiSortOrder(order: SortOrder): ApiSortOrder {
  return order === "asc" ? ApiSortOrder.Asc : ApiSortOrder.Desc;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string | null): value is string {
  return !!value && UUID_RE.test(value);
}

const SESSION_FILTERS = defineFilters([
  {
    id: "date",
    label: "Date range",
    kind: "daterange",
    pinned: true,
    defaultPreset: "30d",
  },
  {
    id: "source",
    label: "Agent type",
    kind: "multiselect",
    allLabel: "All",
  },
  { id: "has_risk", label: "Risk", kind: "select", allLabel: "All" },
  {
    id: "account_type",
    label: "Account type",
    kind: "select",
    allLabel: "All",
    description: "Usage on personal accounts versus team-managed ones.",
  },
  {
    id: "min_risk_score",
    label: "Min risk score",
    kind: "number",
    min: 1,
    placeholder: "e.g. 3 (≥ 3 findings)",
    description: "Only sessions with at least this many risk findings.",
  },
]);

// Static filter options. The "source" (agent type) options are NOT hardcoded
// here — they're derived at render from the sources actually present in the
// project's chats (see useListChatSources below).
const HAS_RISK_OPTIONS: OptionsById = {
  has_risk: [
    { value: "true", label: "With Risk" },
    { value: "false", label: "No Risk" },
  ],
  account_type: [
    { value: "team", label: "Team" },
    { value: "personal", label: "Personal" },
  ],
};

const SESSIONS_VIEW_OPTIONS: {
  value: SessionsView;
  label: ReactNode;
  tooltip: string;
}[] = [
  {
    value: "all",
    label: "All",
    tooltip: "All agent sessions in the current filters",
  },
  {
    value: "pinned",
    label: (
      <span className="inline-flex items-center gap-1.5">
        <Pin className="size-3.5" aria-hidden />
        Pinned
      </span>
    ),
    tooltip: "Sessions you've pinned for quick access",
  },
];

// The page title and description now belong to the ResourceListPage header, so
// they are stated here once and handed to the template.
const AGENT_SESSIONS_TITLE = "Agent Sessions";

// The retention note was part of this page's description before the header
// moved to the template. LoggingPageHeader keeps its info button private, so
// the control is rebuilt here around the message it exports rather than being
// dropped from the description.
const AGENT_SESSIONS_DESCRIPTION = (
  <>
    View and debug individual agent sessions captured for organization members
    in this project{" "}
    <span className="inline-flex align-middle">
      <SimpleTooltip tooltip={LOG_DATA_RETENTION_MESSAGE}>
        <button
          type="button"
          aria-label="About data retention"
          className="text-muted-foreground hover:text-foreground inline-flex cursor-help items-center"
        >
          <Icon name="info" className="size-3.5" />
        </button>
      </SimpleTooltip>
    </span>
  </>
);

// Shown when the caller lacks chat:read: the list is scoped to their own
// sessions and those of the agents they own, so explain why and (for admins)
// point at the roles page where chat:read is granted.
function OwnSessionsNotice(): JSX.Element | null {
  const orgRoutes = useOrgRoutes();
  const { hasScope, isLoading } = useRBAC();

  if (isLoading || hasScope("chat:read")) return null;

  return (
    <Alert variant="info" dismissible={false} className="text-sm">
      Only your own sessions and those of agents you own are shown.{" "}
      <code className="font-mono">chat:read</code> is required to view other
      members&apos; sessions.
      {hasScope("org:admin") && (
        <>
          {" "}
          <Link
            to={orgRoutes.access.roles.href()}
            className="underline underline-offset-2"
          >
            Manage roles
          </Link>
        </>
      )}
    </Alert>
  );
}

export function LogsAgentsContent({
  // The route owns the gate; the template owns the frame it is applied to, and
  // the template is rendered here because every control it takes is derived
  // from this component's state.
  scope,
}: {
  scope?: Scope | Scope[];
}): JSX.Element {
  const { hasAnyScope, isLoading } = useRBAC();
  const scopes = scope == null ? [] : Array.isArray(scope) ? scope : [scope];
  // The gate goes above the content, not inside the template the content
  // renders: the chat, assistant, MCP and Insights reads all fire from the
  // hooks below, so a refusal applied further down still paid for them. The
  // template keeps the `scope` prop and draws the refusal itself, inside the
  // page frame and breadcrumbs — and renders nothing while grants load,
  // which is why loading takes this branch too.
  if (scopes.length > 0 && (isLoading || !hasAnyScope(scopes))) {
    return (
      <ResourceListPage
        scope={scope}
        title={AGENT_SESSIONS_TITLE}
        description={AGENT_SESSIONS_DESCRIPTION}
        hideToolbar
      >
        {null}
      </ResourceListPage>
    );
  }
  return <AgentSessionsContent scope={scope} />;
}

function AgentSessionsContent({
  scope,
}: {
  scope?: Scope | Scope[];
}): JSX.Element {
  const project = useProject();
  const organization = useOrganization();
  const sdk = useSdkClient();
  const { hasScope } = useRBAC();
  const [searchParams, setSearchParams] = useSearchParams();

  const [offset, setOffset] = useState(0);
  const limit = 50;

  const [cachedChat, setCachedChat] = useState<ChatOverview | null>(null);

  const mcpConfig = useObservabilityMcpConfig({
    toolsToInclude: [
      "gram_search_logs",
      "gram_search_chats",
      "gram_get_deployment_logs",
      "gram_load_chat",
      "gram_list_chats",
    ],
  });

  const queryClient = useQueryClient();
  const deleteChatMutation = useChatDeleteMutation();

  const handleDeleteChat = useCallback(
    (chatId: string) => {
      deleteChatMutation.mutate(
        { request: { id: chatId } },
        {
          onSuccess: () => {
            setSearchParams((prev) => {
              if (prev.get(AGENT_SESSION_CHAT_PARAM) !== chatId) return prev;
              const next = new URLSearchParams(prev);
              next.delete(AGENT_SESSION_CHAT_PARAM);
              return next;
            });
            setCachedChat((current) =>
              current?.id === chatId ? null : current,
            );
            void invalidateAllListChats(queryClient);
          },
        },
      );
    },
    [deleteChatMutation, queryClient, setSearchParams],
  );

  const urlRange = searchParams.get("range");
  const urlFrom = searchParams.get("from");
  const urlTo = searchParams.get("to");
  const urlSearch = searchParams.get("search");
  const urlChatId = searchParams.get(AGENT_SESSION_CHAT_PARAM);
  const urlHasRisk = searchParams.get("has_risk");
  const urlAccountType = searchParams.get("account_type");
  const urlPinned = searchParams.get("pinned");
  const urlSource = searchParams.get("source");
  const urlMinRiskScore = searchParams.get("min_risk_score");
  const urlAssistantId = searchParams.get("assistantId");
  const urlAgentId = searchParams.get(AGENT_SESSIONS_AGENT_PARAM);
  const urlSort = searchParams.get("sort") as SortField | null;
  const urlOrder = searchParams.get("order") as SortOrder | null;

  const dateRange: DateRangePreset = isValidPreset(urlRange) ? urlRange : "30d";
  const sortField: SortField =
    urlSort === "messageCount" ? urlSort : "chronological";
  const sortOrder: SortOrder = urlOrder === "asc" ? "asc" : "desc";
  const hasRisk: string =
    urlHasRisk === "true" || urlHasRisk === "false" ? urlHasRisk : "";
  const accountType: string =
    urlAccountType === "team" || urlAccountType === "personal"
      ? urlAccountType
      : "";
  // Pinned sessions get a dedicated view (toolbar segment), not a filter chip —
  // otherwise they disappear into the chronological scroll of All sessions.
  // Legacy ?pinned=false is ignored so the segment stays a two-way All/Pinned.
  const sessionsView: SessionsView = urlPinned === "true" ? "pinned" : "all";
  const minRiskScore = useMemo(
    () => parseMinRiskScore(urlMinRiskScore),
    [urlMinRiskScore],
  );
  const sources = useMemo<string[]>(
    () =>
      urlSource
        ? urlSource
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    [urlSource],
  );

  const customRange = useMemo(() => {
    if (urlFrom && urlTo) {
      const from = new Date(urlFrom);
      const to = new Date(urlTo);
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        return { from, to };
      }
    }
    return null;
  }, [urlFrom, urlTo]);

  const searchQuery = urlSearch ?? "";
  const assistantId = isUuid(urlAssistantId) ? urlAssistantId : "";
  const canReadAssistant =
    !!assistantId && hasScope("assistant:read", assistantId, project.id);

  const { data: assistantData } = useAssistantsGet(
    { id: assistantId, gramProject: project.slug },
    undefined,
    {
      enabled: canReadAssistant,
      retry: false,
      throwOnError: false,
      refetchOnWindowFocus: false,
    },
  );
  const filteredAssistant = canReadAssistant ? assistantData : undefined;

  const agentId = isUuid(urlAgentId) ? urlAgentId : "";
  // Read for the chip's label only. A caller who may list the agent's sessions
  // need not be able to read the agent, so a refusal falls back to the name
  // the session rows carry.
  const { data: agentData } = useQuery({
    queryKey: ["managed-agents", organization.id, "detail", agentId],
    queryKeyHashFn: hashKey,
    queryFn: ({ signal }) =>
      sdk.agents.get({ id: agentId }, undefined, { signal }),
    enabled: !!agentId,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

  const timeRange = useMemo(() => {
    if (customRange) {
      return { from: customRange.from, to: customRange.to };
    }
    return getPresetRange(dateRange);
  }, [customRange, dateRange]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      });
      setOffset(0);
    },
    [setSearchParams],
  );

  const setDateRangeParam = useCallback(
    (preset: DateRangePreset) => {
      updateSearchParams({ range: preset, from: null, to: null });
    },
    [updateSearchParams],
  );

  const setCustomRangeParam = useCallback(
    (from: Date, to: Date) => {
      updateSearchParams({
        range: null,
        from: from.toISOString(),
        to: to.toISOString(),
      });
    },
    [updateSearchParams],
  );

  const clearCustomRange = useCallback(() => {
    updateSearchParams({ from: null, to: null });
  }, [updateSearchParams]);

  const setSearchQuery = useCallback(
    (value: string) => {
      updateSearchParams({ search: value || null });
    },
    [updateSearchParams],
  );

  const setHasRisk = useCallback(
    (value: string) => {
      // "No Risk" means zero findings, which contradicts any positive
      // threshold — clear the score so the two controls never disagree.
      updateSearchParams(
        value === "false"
          ? { has_risk: value, min_risk_score: null }
          : { has_risk: value || null },
      );
    },
    [updateSearchParams],
  );

  const setAccountType = useCallback(
    (value: string) => {
      updateSearchParams({ account_type: value || null });
    },
    [updateSearchParams],
  );

  const setSessionsView = useCallback(
    (view: SessionsView) => {
      updateSearchParams({ pinned: view === "pinned" ? "true" : null });
    },
    [updateSearchParams],
  );

  const setSources = useCallback(
    (values: string[]) => {
      updateSearchParams({
        source: values.length ? values.join(",") : null,
      });
    },
    [updateSearchParams],
  );

  const setMinRiskScore = useCallback(
    (value: number | null) => {
      // A threshold below 1 ("≥ 0" = everything) is meaningless, so treat it as
      // clearing the filter rather than sending a 0 the API rejects.
      const threshold =
        value !== null && Number.isInteger(value) && value >= 1 ? value : null;
      // Entering a real threshold contradicts the "No Risk" presence option,
      // so drop that selection when a score is set.
      const clearNoRisk = threshold !== null && hasRisk === "false";
      updateSearchParams({
        min_risk_score: threshold === null ? null : String(threshold),
        ...(clearNoRisk ? { has_risk: null } : {}),
      });
    },
    [updateSearchParams, hasRisk],
  );

  // Single setSearchParams so the synchronous clears don't clobber each other
  // (react-router's setSearchParams reads a memoized snapshot). The All/Pinned
  // view segment is independent of filters, so Reset leaves `pinned` alone.
  const clearAllFilters = useCallback(() => {
    updateSearchParams({
      range: null,
      from: null,
      to: null,
      has_risk: null,
      account_type: null,
      source: null,
      min_risk_score: null,
    });
  }, [updateSearchParams]);

  const clearAssistantFilter = useCallback(() => {
    updateSearchParams({ assistantId: null });
  }, [updateSearchParams]);

  const clearAgentFilter = useCallback(() => {
    updateSearchParams({ [AGENT_SESSIONS_AGENT_PARAM]: null });
  }, [updateSearchParams]);

  const setSortField = useCallback(
    (value: SortField) => {
      updateSearchParams({ sort: value === "chronological" ? null : value });
    },
    [updateSearchParams],
  );

  const setSortOrder = useCallback(
    (value: SortOrder) => {
      updateSearchParams({ order: value === "desc" ? null : value });
    },
    [updateSearchParams],
  );

  // The column headers and the toolbar's own control are two ways of saying
  // the same thing, so they write the same two parameters and the URL stays
  // the single answer to "what order is this in" — which is also what makes
  // an order shareable.
  const sort = useMemo<SortDescriptor>(
    () => ({ id: sortField, direction: sortOrder }),
    [sortField, sortOrder],
  );

  const handleSortChange = useCallback(
    (next: SortDescriptor | null) => {
      // Only the two columns the API can order by are sortable, so anything
      // else is a column id this page has no query for.
      if (next?.id !== "chronological" && next?.id !== "messageCount") return;
      updateSearchParams({
        sort: next.id === "chronological" ? null : next.id,
        order: next.direction === "desc" ? null : next.direction,
      });
    },
    [updateSearchParams],
  );

  const { data, isLoading, isFetching, error, refetch, isLogsDisabled } =
    useLogsEnabledErrorCheck(
      useListChats(
        {
          search: searchQuery || undefined,
          // A custom threshold supersedes the binary presence filter: "count >
          // n" (n >= 0) already implies risk is present, so we don't also send
          // has_risk and risk a contradictory pair.
          hasRisk:
            minRiskScore !== undefined ? undefined : toApiHasRisk(hasRisk),
          minRiskScore,
          accountType: toApiAccountType(accountType),
          pinned: toApiPinned(sessionsView === "pinned" ? "true" : ""),
          assistantId: assistantId || undefined,
          agentId: agentId || undefined,
          source: sources.length ? sources.join(",") : undefined,
          from: timeRange.from,
          to: timeRange.to,
          sortBy: toApiSortBy(sortField),
          sortOrder: toApiSortOrder(sortOrder),
          limit,
          offset,
        },
        undefined,
        { throwOnError: false },
      ),
    );

  const chats = useMemo(() => data?.chats ?? [], [data?.chats]);
  const filteredAgentName =
    agentData?.name ??
    chats.find((chat) => chat.agentId === agentId)?.agentName ??
    null;

  // Agent-type filter options derived from the sources actually present in the
  // project's chats, so the list stays in sync with the data.
  const { data: sourcesData } = useListChatSources(undefined, undefined, {
    throwOnError: false,
  });
  const filterOptions = useMemo<OptionsById>(
    () => ({
      ...HAS_RISK_OPTIONS,
      source: (sourcesData?.sources ?? []).map((s) => ({
        value: s,
        label: formatPlatform(s),
      })),
    }),
    [sourcesData?.sources],
  );

  // Cache the last known total so pagination stays stable while a refetch is
  // in flight (data briefly undefined). Include 0 so an empty view — e.g.
  // Pinned after unpinning the last session — does not keep the previous
  // view's count and leave Next enabled.
  const lastTotalRef = useRef(0);
  if (data?.total !== undefined) {
    lastTotalRef.current = data.total;
  }
  const total = lastTotalRef.current;
  const hasMore =
    total > 0 ? offset + chats.length < total : chats.length === limit;

  const selectedChat = useMemo<ChatOverview | null>(() => {
    if (!urlChatId) return null;
    const fromList = chats.find((c) => c.id === urlChatId);
    if (fromList) return fromList;
    if (cachedChat?.id === urlChatId) return cachedChat;
    return null;
  }, [urlChatId, chats, cachedChat]);

  useEffect(() => {
    if (!urlChatId) {
      if (cachedChat) setCachedChat(null);
      return;
    }
    const fromList = chats.find((c) => c.id === urlChatId);
    if (fromList && fromList !== cachedChat) {
      setCachedChat(fromList);
    }
  }, [urlChatId, chats, cachedChat]);

  const setSelectedChat = useCallback(
    (chat: ChatOverview | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (chat) {
          next.set(AGENT_SESSION_CHAT_PARAM, chat.id);
        } else {
          next.delete(AGENT_SESSION_CHAT_PARAM);
        }
        return next;
      });
      if (chat) setCachedChat(chat);
    },
    [setSearchParams],
  );

  // Open a chat by bare id — used by the detail panel's Linked-sessions rows,
  // whose far end may not be on the current list page, so there is no
  // ChatOverview to hand to setSelectedChat. The panel loads by id.
  const openChatByID = useCallback(
    (chatID: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(AGENT_SESSION_CHAT_PARAM, chatID);
        return next;
      });
    },
    [setSearchParams],
  );

  const dateRangeContext = useMemo(() => {
    const formatDate = (d: Date) =>
      d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    return `Viewing logs from ${formatDate(timeRange.from)} to ${formatDate(timeRange.to)}${
      searchQuery ? ` Search query: "${searchQuery}"` : ""
    }${
      filteredAssistant
        ? `. Scoped to assistant "${filteredAssistant.name}".`
        : ""
    }${filteredAgentName ? `. Scoped to agent "${filteredAgentName}".` : ""}`;
  }, [
    timeRange.from,
    timeRange.to,
    searchQuery,
    filteredAssistant,
    filteredAgentName,
  ]);

  return (
    <>
      <InsightsConfig
        mcpConfig={mcpConfig}
        title="How can I help you debug?"
        subtitle="Search agent sessions, analyze failures, or explore logs"
        contextInfo={dateRangeContext}
        // Hide the docked assistant on this page — the agent-sessions list and
        // its detail drawer are the primary surface here.
        hideTrigger
        suggestions={INSIGHTS_SUGGESTIONS["agent-sessions"]}
      />
      <AgentSessionsPageContent
        scope={scope}
        dateRange={dateRange}
        setDateRangeParam={setDateRangeParam}
        setCustomRangeParam={setCustomRangeParam}
        customRange={customRange}
        clearCustomRange={clearCustomRange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        hasRisk={hasRisk}
        setHasRisk={setHasRisk}
        accountType={accountType}
        setAccountType={setAccountType}
        sessionsView={sessionsView}
        setSessionsView={setSessionsView}
        sources={sources}
        setSources={setSources}
        filterOptions={filterOptions}
        minRiskScore={minRiskScore}
        setMinRiskScore={setMinRiskScore}
        clearAllFilters={clearAllFilters}
        assistantName={filteredAssistant?.name ?? null}
        hasAssistantFilter={!!assistantId}
        clearAssistantFilter={clearAssistantFilter}
        agentName={filteredAgentName}
        hasAgentFilter={!!agentId}
        clearAgentFilter={clearAgentFilter}
        sortField={sortField}
        setSortField={setSortField}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        sort={sort}
        onSortChange={handleSortChange}
        chats={chats}
        selectedChat={selectedChat}
        selectedChatId={urlChatId}
        setSelectedChat={setSelectedChat}
        onOpenChat={openChatByID}
        isLoading={isLoading}
        error={error}
        isLogsDisabled={isLogsDisabled}
        onLogsEnabled={() => void refetch()}
        onRefresh={() => void refetch()}
        isRefreshing={isFetching}
        hasMore={hasMore}
        offset={offset}
        setOffset={setOffset}
        limit={limit}
        total={total}
        onDeleteChat={handleDeleteChat}
      />
    </>
  );
}

// Names a scope the list arrived narrowed to from another page. It is a chip
// rather than a filter in the toolbar because nothing on this page sets it.
function ScopeChip({
  label,
  name,
  onClear,
}: {
  label: string;
  name: string;
  onClear: () => void;
}): JSX.Element {
  return (
    <Badge variant="neutral" className="w-fit gap-1.5 px-2.5 py-1 text-xs">
      <Icon name="bot" className="size-3" />
      <span>
        {label}: <span className="font-medium">{name}</span>
      </span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label.toLowerCase()} filter`}
        className="hover:bg-muted-foreground/20 -mr-1 ml-0.5 flex size-4 items-center justify-center"
      >
        <Icon name="x" className="size-3" />
      </button>
    </Badge>
  );
}

function AgentSessionsPageContent({
  scope,
  dateRange,
  setDateRangeParam,
  setCustomRangeParam,
  customRange,
  clearCustomRange,
  searchQuery,
  setSearchQuery,
  hasRisk,
  setHasRisk,
  accountType,
  setAccountType,
  sessionsView,
  setSessionsView,
  sources,
  setSources,
  filterOptions,
  minRiskScore,
  setMinRiskScore,
  clearAllFilters,
  assistantName,
  hasAssistantFilter,
  clearAssistantFilter,
  agentName,
  hasAgentFilter,
  clearAgentFilter,
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  chats,
  selectedChat,
  selectedChatId,
  setSelectedChat,
  onOpenChat,
  isLoading,
  error,
  isLogsDisabled,
  onLogsEnabled,
  onRefresh,
  isRefreshing,
  hasMore,
  sort,
  onSortChange,
  offset,
  setOffset,
  limit,
  total,
  onDeleteChat,
}: {
  scope?: Scope | Scope[];
  dateRange: DateRangePreset;
  setDateRangeParam: (preset: DateRangePreset) => void;
  setCustomRangeParam: (from: Date, to: Date) => void;
  customRange: { from: Date; to: Date } | null;
  clearCustomRange: () => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  hasRisk: string;
  setHasRisk: (value: string) => void;
  accountType: string;
  setAccountType: (value: string) => void;
  sessionsView: SessionsView;
  setSessionsView: (view: SessionsView) => void;
  sources: string[];
  setSources: (values: string[]) => void;
  filterOptions: OptionsById;
  minRiskScore: number | undefined;
  setMinRiskScore: (value: number | null) => void;
  clearAllFilters: () => void;
  assistantName: string | null;
  hasAssistantFilter: boolean;
  clearAssistantFilter: () => void;
  agentName: string | null;
  hasAgentFilter: boolean;
  clearAgentFilter: () => void;
  sortField: SortField;
  setSortField: (value: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (value: SortOrder) => void;
  chats: ChatOverview[];
  selectedChat: ChatOverview | null;
  selectedChatId: string | null;
  setSelectedChat: (chat: ChatOverview | null) => void;
  onOpenChat: (chatID: string) => void;
  isLoading: boolean;
  error: Error | null;
  isLogsDisabled: boolean;
  onLogsEnabled: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  hasMore: boolean;
  sort: SortDescriptor;
  onSortChange: (sort: SortDescriptor | null) => void;
  offset: number;
  setOffset: (offset: number) => void;
  limit: number;
  total: number;
  onDeleteChat: (chatId: string) => void;
}) {
  return (
    <ResourceListPage
      scope={scope}
      title={AGENT_SESSIONS_TITLE}
      description={AGENT_SESSIONS_DESCRIPTION}
      // Logging off means there is no list and nothing to filter, so the
      // overlay takes the body and the template drops the toolbar with it.
      isEmpty={isLogsDisabled}
      empty={
        <EnableLoggingOverlay
          onEnabled={onLogsEnabled}
          screenshotSrc="/empty-states/agent_sessions_empty.png"
          screenshotAlt="Agent Sessions dashboard with recorded sessions"
        />
      }
      // Above the toolbar, because the chip says which sessions the controls
      // below are narrowing. Suppressed with the rest of the list while
      // logging is off, where it would describe a filter on nothing.
      belowHeader={
        (hasAssistantFilter || hasAgentFilter) && !isLogsDisabled ? (
          <div className="mb-4 flex flex-wrap gap-2">
            {hasAssistantFilter && (
              <ScopeChip
                label="Assistant"
                name={assistantName ?? "Loading…"}
                onClear={clearAssistantFilter}
              />
            )}
            {hasAgentFilter && (
              <ScopeChip
                label="Agent"
                name={agentName ?? "Unknown agent"}
                onClear={clearAgentFilter}
              />
            )}
          </div>
        ) : null
      }
      search={{
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: "Search by chat ID, user ID, user name, or title...",
        debounceMs: 500,
      }}
      filters={{
        schema: SESSION_FILTERS,
        values: {
          date: {
            preset: customRange ? null : dateRange,
            customRange,
            customLabel: null,
          },
          has_risk: hasRisk || null,
          account_type: accountType || null,
          source: sources,
          min_risk_score: minRiskScore ?? null,
        },
        optionsById: filterOptions,
        onChange: (id: string, value: FilterValue) => {
          if (id === "date") {
            const dateValue = value as {
              preset: DateRangePreset | null;
              customRange: { from: Date; to: Date } | null;
            };
            if (dateValue.customRange) {
              setCustomRangeParam(
                dateValue.customRange.from,
                dateValue.customRange.to,
              );
            } else if (dateValue.preset) {
              setDateRangeParam(dateValue.preset);
            } else {
              clearCustomRange();
            }
          } else if (id === "has_risk") {
            setHasRisk((value as string | null) ?? "");
          } else if (id === "account_type") {
            setAccountType((value as string | null) ?? "");
          } else if (id === "source") {
            setSources((value as string[]) ?? []);
          } else if (id === "min_risk_score") {
            setMinRiskScore(value as number | null);
          }
        },
        onClear: (id: string) => {
          if (id === "date") {
            setDateRangeParam("30d");
          } else if (id === "has_risk") {
            setHasRisk("");
          } else if (id === "account_type") {
            setAccountType("");
          } else if (id === "source") {
            setSources([]);
          } else if (id === "min_risk_score") {
            setMinRiskScore(null);
          }
        },
        onClearAll: clearAllFilters,
      }}
      sort={{
        value: sortField,
        onChange: (v) => setSortField(v as SortField),
        options: [
          { value: "chronological", label: "Date" },
          { value: "messageCount", label: "Message Count" },
        ],
        direction: sortOrder,
        onDirectionChange: setSortOrder,
      }}
      // The All/Pinned segment is a view, not a filter, so it keeps its place
      // beside the controls rather than joining the filter chips.
      toolbarActions={
        <SegmentedControl
          value={sessionsView}
          onChange={setSessionsView}
          options={SESSIONS_VIEW_OPTIONS}
        />
      }
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
    >
      <div className="space-y-4">
        <OwnSessionsNotice />
        {/* The count the server reports for these filters, not the length of
              this page: the table shows one page of 50 at a time. It is held
              back while the first page is in flight, where "0 rows" would be a
              statement about the project rather than about a pending read. */}
        {!isLoading && (
          <span className="text-muted-foreground block font-mono text-xs tabular-nums">
            {total} {total === 1 ? "row" : "rows"}
          </span>
        )}
        {/* The table draws no rules of its own, so the border lives here —
              the lines the old full-bleed shell drew above and below it. */}
        <div className="border-border border">
          <ChatLogsTable
            chats={chats}
            selectedChatId={selectedChat?.id}
            onSelectChat={setSelectedChat}
            onDeleteChat={onDeleteChat}
            isLoading={isLoading}
            error={error}
            sort={sort}
            onSortChange={onSortChange}
            emptyState={
              sessionsView === "pinned"
                ? {
                    title: "No pinned sessions",
                    description:
                      "Pin a session from All to keep it here for quick access.",
                  }
                : undefined
            }
          />
          {(hasMore || offset > 0) && (
            <div className="bg-card flex items-center justify-center gap-4 border-t p-4">
              <Button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <span className="text-muted-foreground font-mono text-xs tabular-nums">
                Page {Math.floor(offset / limit) + 1}
                {total > 0 && ` of ${Math.ceil(total / limit)}`}
              </span>
              <Button
                onClick={() => setOffset(offset + limit)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* A sibling of the list rather than of the page: the drawer opens a
            row from this table, so it belongs to the same branch the table
            does and goes away with it when logging is off. */}
      <ChatDetailSheet
        chatId={selectedChatId ?? selectedChat?.id ?? null}
        onClose={() => setSelectedChat(null)}
        onDelete={onDeleteChat}
        onOpenChat={onOpenChat}
        dimNonRisk={hasRisk === "true" || minRiskScore !== undefined}
      />
    </ResourceListPage>
  );
}
